import React, { useEffect } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { GlobalStyles, ThemeProvider } from "@mui/material";
import { createTheme } from "@mui/material/styles";
import RegistrationPage from "./RegistrationPage";
/**
 * QA-17 — NO ApolloProvider / ApolloClient IS CREATED HERE, DELIBERATELY.
 *
 * `AppWrapper.jsx` owns the single configured client and wraps <App/> in its ApolloProvider.
 * This file previously created a SECOND, bare client (`new ApolloClient({ uri, cache })`) and
 * nested its own provider inside that one. React context resolves to the NEAREST provider, so
 * the bare client won every query and AppWrapper's configuration was inert:
 *
 *   · errorLink                     never fired — GraphQL/network errors went unlogged
 *   · credentials: "same-origin"    never applied
 *   · REACT_APP_API_URL override    ignored; the hardcoded constant was always used
 *   · defaultOptions                ignored (errorPolicy "all", fetchPolicy per operation)
 *   · InMemoryCache typePolicies    ignored — two caches existed, only one was configured
 *
 * The bug stayed invisible because both clients happened to target the same production URL, so
 * queries worked BY COINCIDENCE. Anything that depends on the link chain — an auth header, an
 * environment override, error handling — would have silently no-opped.
 *
 * Do NOT reintroduce a client here. If a query needs different behaviour, configure it in
 * AppWrapper's client or per-operation; a second client would restore the shadowing.
 *
 * `API_URL` is no longer imported either — it existed solely to feed the bare client, and
 * AppWrapper resolves the endpoint itself (env override first, then the same default).
 */
import ContactPage from "./ContactPage";
import RegistrationIframe from "./RegistrationIframe";
import EditRegistration from "./EditRegistration";
import PaymentStatusPage from "./PaymentStatusPage";
import AgendaPreview from "./AgendaPreview";
import NotFound from "./Components/NotFound";
import LinkNotFound from "./Components/LinkNotFound";
import SurveyForm from "./SurveyForm";
import PublicSurveyPage from "./PublicSurveyPage";
import SearchBadge from "./SearchBadge";
import SearchCertificate from "./SearchCertificate";
import MobileOtpBadgeDownload from "./MobileOtpBadgeDownload";
import PaymentForm from "./Pay";
import PaymentResponsePage from "./PaymentResponse";
import SocialSharePage from "./SocialSharePage";
import ShareGeneratorPage from "./ShareGeneratorPage";
import CertificateVerifyPage from "./CertificateVerifyPage";
import AddonsPage from "./AddonsPage";
import EventRedirect from "./EventRedirect";
import SocialAuthCallback from "./SocialAuthCallback";
import SocialAuthPopup from "./SocialAuthPopup";
import FloorPlan from "./FloorPlan";
import { HelmetProvider } from "react-helmet-async";

const theme = createTheme();

export default function App() {
  return (
    <>
      <HelmetProvider>
        <ThemeProvider theme={theme}>
          <GlobalStyles
            styles={{
              "*::-webkit-scrollbar": {
                width: 20,
              },
              "*::-webkit-scrollbar-track": {
                backgroundColor: "transparent",
              },
              "*::-webkit-scrollbar-thumb": {
                backgroundColor: "#d6dee1",
                borderRadius: 20,
                border: "6px solid transparent",
                backgroundClip: "content-box",
              },
              "*::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "#a8bbbf",
              },
            }}
          />
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Website />} />
              {/* <Route path="/" element={<NotFound />} /> */}
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/paymentstatus" element={<PaymentStatusPage />} />
              <Route path="/socialShare" element={<SocialSharePage />} />
              <Route
                path="/events/:eventId/:role"
                element={<EventRedirect />}
              />
              <Route
                path="/social-auth/:provider"
                element={<SocialAuthCallback />}
              />
              <Route path="/social-auth/popup" element={<SocialAuthPopup />} />
              {/* R44: public Social Share Generator (must precede the catch-all /:link).
                  Per-role (owner 2026-07-13): /share/:eventId is the event-wide config;
                  /share/:eventId/:role loads that role's own setup. */}
              <Route path="/share/:eventId" element={<ShareGeneratorPage />} />
              <Route path="/share/:eventId/:role" element={<ShareGeneratorPage />} />
              {/* EPI P3: public certificate verification (must precede the catch-all /:link). */}
              <Route path="/verify/:eventId/:certId" element={<CertificateVerifyPage />} />
              {/* Public add-on catalogue. MUST precede the catch-all /:link below — that route
                  matches any single segment, so a later declaration would render the
                  registration form for /addons instead. Read-only: no checkout is wired while
                  QA-16 (gateway secret readable here) and QA-18 (ledger truncates 3-decimal OMR)
                  remain open. */}
              <Route path="/addons/:link" element={<AddonsPage />} />
              <Route path="/:link" element={<RegistrationPage />} />
              <Route path="/:link/:id/edit" element={<EditRegistration />} />
              <Route path="/iframe/:link" element={<RegistrationIframe />} />
              <Route path="/agenda/:link" element={<AgendaPreview />} />
              {/*
                TWO survey routes, and the segment count is what keeps them apart.

                `/survey/:link`            — the LEGACY registration-bound post-event survey. Live for
                                             real events; deliberately untouched.
                `/survey/:eventId/:slug`   — the self-serve Survey Builder form (anonymous, public).

                The event id is not padding: survey slugs are unique per EVENT, so two events may both
                own `delegate-experience-2026` and a slug alone cannot identify one. React Router matches
                on segment count, so neither route can shadow the other.
              */}
              <Route path="/survey/:link" element={<SurveyForm />} />
              <Route path="/survey/:eventId/:slug" element={<PublicSurveyPage />} />
              <Route path="/search/:eventId" element={<SearchBadge />} />
              <Route
                path="/verification-badge/:eventId/:mobile/:role/:id"
                element={<MobileOtpBadgeDownload />}
              />
              <Route path="/cert/:eventId" element={<SearchCertificate />} />
              <Route path="/payment/pay" element={<PaymentForm />} />
              <Route path="/payment-response" component={PaymentResponsePage} />
              <Route path="/floorplan/:eventId" element={<FloorPlan />} />

              <Route path="*" element={<LinkNotFound />} />
            </Routes>
          </BrowserRouter>
        </ThemeProvider>
      </HelmetProvider>
    </>
  );
}

function Website() {
  // 👇️ redirect to external URL
  window.location.replace("https://eventegration.com/website/index.php");

  return null;
}
